<template>
  <div class="p-6">
    <!-- Header -->
    <div class="mb-8">
      <div class="flex items-center justify-end">
        <div class="flex space-x-3">
          <button
            @click="refreshSessions"
            :disabled="loading"
            class="btn-secondary flex items-center"
          >
            <RefreshCw :class="['w-4 h-4 mr-2', loading && 'animate-spin']" />
            Refresh
          </button>
          <button @click="exportReport" class="btn-primary flex items-center">
            <Download class="w-4 h-4 mr-2" />
            Export Report
          </button>
        </div>
      </div>
    </div>

    <!-- Stats Cards -->
    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <Users class="h-8 w-8 text-green-600" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Active Sessions</p>
            <p class="text-2xl font-bold text-gray-900">
              {{ sessionStats.activeSessions }}
            </p>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <Shield class="h-8 w-8 text-blue-600" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Secure Sessions</p>
            <p class="text-2xl font-bold text-gray-900">
              {{ sessionStats.secureSessions }}
            </p>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <AlertTriangle class="h-8 w-8 text-yellow-600" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Suspicious Sessions</p>
            <p class="text-2xl font-bold text-gray-900">
              {{ sessionStats.suspiciousSessions }}
            </p>
          </div>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow p-6">
        <div class="flex items-center">
          <div class="flex-shrink-0">
            <Clock class="h-8 w-8 text-purple-600" />
          </div>
          <div class="ml-4">
            <p class="text-sm font-medium text-gray-500">Avg. Duration</p>
            <p class="text-2xl font-bold text-gray-900">
              {{ sessionStats.avgDuration }}
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Filters -->
    <div class="bg-white rounded-lg shadow p-6 mb-6">
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <div>
          <label class="form-label">Search User</label>
          <input
            v-model="filters.search"
            type="text"
            placeholder="Search by name or email..."
            class="form-input"
          />
        </div>
        <div>
          <label class="form-label">Status</label>
          <select v-model="filters.status" class="form-select">
            <option value="">All Sessions</option>
            <option value="active">Active</option>
            <option value="expired">Expired</option>
            <option value="suspicious">Suspicious</option>
          </select>
        </div>
        <div>
          <label class="form-label">Device Type</label>
          <select v-model="filters.deviceType" class="form-select">
            <option value="">All Devices</option>
            <option value="desktop">Desktop</option>
            <option value="mobile">Mobile</option>
            <option value="tablet">Tablet</option>
          </select>
        </div>
        <div>
          <label class="form-label">Location</label>
          <select v-model="filters.location" class="form-select">
            <option value="">All Locations</option>
            <option value="office">Office</option>
            <option value="remote">Remote</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
      </div>
    </div>

    <!-- Session Table -->
    <div class="bg-white rounded-lg shadow overflow-hidden">
      <div class="px-6 py-4 border-b border-gray-200">
        <h3 class="text-lg font-medium text-gray-900">Active Sessions</h3>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                User
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Device & Location
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Session Info
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Last Activity
              </th>
              <th
                class="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-if="loading">
              <td colspan="6" class="px-6 py-12 text-center">
                <RefreshCw class="w-6 h-6 animate-spin mx-auto text-gray-400" />
                <p class="text-gray-500 mt-2">Loading sessions...</p>
              </td>
            </tr>
            <tr v-else-if="formattedSessions.length === 0">
              <td colspan="6" class="px-6 py-12 text-center text-gray-500">
                No sessions found matching your filters.
              </td>
            </tr>
            <tr
              v-else
              v-for="session in formattedSessions"
              :key="session.id"
              class="hover:bg-gray-50"
            >
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <div class="flex-shrink-0 h-10 w-10">
                    <div
                      class="h-10 w-10 rounded-full bg-gray-300 flex items-center justify-center"
                    >
                      <User class="h-6 w-6 text-gray-600" />
                    </div>
                  </div>
                  <div class="ml-4">
                    <div class="text-sm font-medium text-gray-900">
                      {{ session.user.name }}
                    </div>
                    <div class="text-sm text-gray-500">
                      {{ session.user.email }}
                    </div>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">
                  <div class="flex items-center">
                    <component
                      :is="getDeviceIcon(session.device.type)"
                      class="w-4 h-4 mr-2 text-gray-500"
                    />
                    {{ session.device.name }}
                  </div>
                  <div class="text-xs text-gray-500 mt-1">
                    {{ session.locationDisplay }}
                  </div>
                  <div
                    v-if="session.isOfficeNetwork"
                    class="text-xs text-green-600 mt-1"
                  >
                    Office Network
                  </div>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="text-sm text-gray-900">
                  Duration: {{ session.formattedDuration }}
                </div>
                <div class="text-xs text-gray-500">
                  Started: {{ formatDate(session.startTime) }}
                </div>
                <div
                  v-if="session.securityScore"
                  class="text-xs text-gray-600 mt-1"
                >
                  Security: {{ session.securityScore }}/100
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span
                  :class="[
                    'inline-flex px-2 py-1 text-xs font-semibold rounded-full',
                    session.statusColorClass,
                  ]"
                >
                  {{ session.status }}
                </span>
                <div v-if="session.suspicious" class="flex items-center mt-1">
                  <AlertTriangle class="w-3 h-3 text-red-500 mr-1" />
                  <span class="text-xs text-red-600"
                    >{{ session.securityAlerts?.length || 0 }} Alert(s)</span
                  >
                </div>
                <div v-if="session.riskLevel !== 'low'" class="mt-1">
                  <span
                    :class="[
                      'inline-flex px-1 py-0.5 text-xs rounded',
                      session.riskColorClass,
                    ]"
                  >
                    {{ session.riskLevel }} risk
                  </span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                {{ session.relativeLastActivity }}
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm font-medium">
                <div class="flex space-x-2">
                  <button
                    @click="viewSessionDetails(session)"
                    class="text-blue-600 hover:text-blue-900"
                    title="View Details"
                  >
                    <Eye class="w-4 h-4" />
                  </button>
                  <button
                    @click="terminateSession(session)"
                    class="text-red-600 hover:text-red-900"
                    :disabled="session.status === 'expired'"
                    title="Terminate Session"
                  >
                    <X class="w-4 h-4" />
                  </button>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Pagination -->
      <div
        class="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6"
      >
        <div class="flex-1 flex justify-between sm:hidden">
          <button
            @click="previousPage"
            :disabled="pagination.currentPage === 1"
            class="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Previous
          </button>
          <button
            @click="nextPage"
            :disabled="pagination.currentPage === pagination.totalPages"
            class="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
          >
            Next
          </button>
        </div>
        <div
          class="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between"
        >
          <div>
            <p class="text-sm text-gray-700">
              Showing
              {{ (pagination.currentPage - 1) * pagination.itemsPerPage + 1 }}
              to
              {{
                Math.min(
                  pagination.currentPage * pagination.itemsPerPage,
                  pagination.total
                )
              }}
              of {{ pagination.total }} results
            </p>
          </div>
          <div>
            <nav
              class="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
            >
              <button
                @click="previousPage"
                :disabled="pagination.currentPage === 1"
                class="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Previous
              </button>
              <button
                v-for="page in visiblePages"
                :key="page"
                @click="goToPage(page)"
                :class="[
                  'relative inline-flex items-center px-4 py-2 border text-sm font-medium',
                  page === pagination.currentPage
                    ? 'z-10 bg-blue-50 border-blue-500 text-blue-600'
                    : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50',
                ]"
              >
                {{ page }}
              </button>
              <button
                @click="nextPage"
                :disabled="pagination.currentPage === pagination.totalPages"
                class="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
              >
                Next
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

    <!-- Session Details Modal -->
    <div
      v-if="showDetailsModal"
      class="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50"
      @click="closeDetailsModal"
    >
      <div
        class="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white"
        @click.stop
      >
        <div class="flex items-center justify-between mb-4">
          <h3 class="text-lg font-medium text-gray-900">Session Details</h3>
          <button
            @click="closeDetailsModal"
            class="text-gray-400 hover:text-gray-600"
          >
            <X class="w-6 h-6" />
          </button>
        </div>

        <div v-if="selectedSession" class="space-y-4">
          <div class="grid grid-cols-2 gap-4">
            <div>
              <h4 class="font-medium text-gray-900">User Information</h4>
              <p class="text-sm text-gray-600">
                {{ selectedSession.user.name }}
              </p>
              <p class="text-sm text-gray-600">
                {{ selectedSession.user.email }}
              </p>
              <p class="text-sm text-gray-600">
                Role: {{ selectedSession.user.role }}
              </p>
            </div>
            <div>
              <h4 class="font-medium text-gray-900">Session Information</h4>
              <p class="text-sm text-gray-600">ID: {{ selectedSession.id }}</p>
              <p class="text-sm text-gray-600">
                Started: {{ formatDate(selectedSession.startTime) }}
              </p>
              <p class="text-sm text-gray-600">
                Duration:
                {{ sessionApi.formatDuration(selectedSession.duration) }}
              </p>
              <p
                v-if="selectedSession.securityScore"
                class="text-sm text-gray-600"
              >
                Security Score: {{ selectedSession.securityScore }}/100
              </p>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-4">
            <div>
              <h4 class="font-medium text-gray-900">Device Information</h4>
              <p class="text-sm text-gray-600">
                {{ selectedSession.device.name }}
              </p>
              <p class="text-sm text-gray-600">
                OS: {{ selectedSession.device.os }}
              </p>
              <p class="text-sm text-gray-600">
                Browser: {{ selectedSession.device.browser }}
              </p>
              <p
                v-if="selectedSession.device.fingerprint"
                class="text-sm text-gray-600"
              >
                Fingerprint:
                {{ selectedSession.device.fingerprint?.substring(0, 12) }}...
              </p>
            </div>
            <div>
              <h4 class="font-medium text-gray-900">Location Information</h4>
              <p class="text-sm text-gray-600">
                {{ sessionApi.getLocationDisplay(selectedSession.location) }}
              </p>
              <p class="text-sm text-gray-600">
                IP: {{ selectedSession.location.ip }}
              </p>
              <p class="text-sm text-gray-600">
                ISP: {{ selectedSession.location.isp || 'Unknown' }}
              </p>
              <div
                v-if="sessionApi.isOfficeNetwork(selectedSession.location.ip)"
                class="text-sm text-green-600"
              >
                ✓ Office Network
              </div>
            </div>
          </div>

          <div
            v-if="
              selectedSession.suspicious &&
              selectedSession.securityAlerts?.length > 0
            "
          >
            <h4 class="font-medium text-gray-900 text-red-600">
              Security Alerts
            </h4>
            <div class="bg-red-50 border border-red-200 rounded p-3">
              <ul class="text-sm text-red-700 space-y-1">
                <li
                  v-for="alert in selectedSession.securityAlerts"
                  :key="alert.type || alert"
                >
                  • {{ typeof alert === 'object' ? alert.description : alert }}
                </li>
              </ul>
            </div>
          </div>

          <div class="flex justify-end space-x-3 pt-4 border-t">
            <button @click="closeDetailsModal" class="btn-secondary">
              Close
            </button>
            <button
              v-if="selectedSession.status === 'active'"
              @click="terminateSession(selectedSession)"
              class="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-md"
            >
              Terminate Session
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Notifications -->
    <div
      v-if="notification.show"
      :class="[
        'fixed top-4 right-4 z-50 p-4 rounded-md shadow-lg transition-all duration-300',
        notification.type === 'success'
          ? 'bg-green-100 text-green-800 border-green-200'
          : 'bg-red-100 text-red-800 border-red-200',
      ]"
    >
      <div class="flex items-center">
        <span class="flex-1">{{ notification.message }}</span>
        <button
          @click="notification.show = false"
          class="ml-2 text-gray-500 hover:text-gray-700"
        >
          <X class="w-4 h-4" />
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted, onUnmounted, watch } from 'vue';
import {
  RefreshCw,
  Download,
  Users,
  Shield,
  AlertTriangle,
  Clock,
  User,
  Eye,
  X,
  Monitor,
  Smartphone,
  Tablet,
} from 'lucide-vue-next';

// Import the session API service
import { sessionApi } from '@/services/sessionApi';

export default {
  name: 'AdminSessions',
  components: {
    RefreshCw,
    Download,
    Users,
    Shield,
    AlertTriangle,
    Clock,
    User,
    Eye,
    X,
    Monitor,
    Smartphone,
    Tablet,
  },
  setup() {
    // Reactive state
    const loading = ref(false);
    const showDetailsModal = ref(false);
    const selectedSession = ref(null);
    const sessions = ref([]);

    // Session stats
    const sessionStats = ref({
      activeSessions: 0,
      secureSessions: 0,
      suspiciousSessions: 0,
      avgDuration: '0h 0m',
    });

    // Filters
    const filters = ref({
      search: '',
      status: '',
      deviceType: '',
      location: '',
    });

    // Pagination
    const pagination = ref({
      currentPage: 1,
      itemsPerPage: 10,
      totalPages: 1,
      total: 0,
    });

    // Notification system
    const notification = ref({
      show: false,
      message: '',
      type: 'success',
    });

    // Auto-refresh interval
    let refreshInterval = null;

    // Computed properties
    const visiblePages = computed(() => {
      const pages = [];
      const total = pagination.value.totalPages;
      const current = pagination.value.currentPage;

      let start = Math.max(1, current - 2);
      let end = Math.min(total, current + 2);

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }

      return pages;
    });

    // Format sessions for display with utility functions
    const formattedSessions = computed(() => {
      if (!sessions.value || !Array.isArray(sessions.value)) {
        return [];
      }
      return sessionApi.formatSessionsForTable(sessions.value);
    });

    // Methods - Updated with proper error handling
    const refreshSessions = async (showLoader = true) => {
      if (showLoader) loading.value = true;

      try {
        // Fetch both sessions and stats in parallel
        const [sessionsResponse, statsResponse] = await Promise.all([
          sessionApi.getAllSessions({
            page: pagination.value.currentPage,
            limit: pagination.value.itemsPerPage,
            search: filters.value.search || undefined,
            status: filters.value.status || undefined,
            deviceType: filters.value.deviceType || undefined,
            location: filters.value.location || undefined,
          }),
          sessionApi.getSessionStats(),
        ]);

        if (sessionsResponse.success) {
          sessions.value = sessionsResponse.data.sessions || [];
          pagination.value = {
            ...pagination.value,
            currentPage: sessionsResponse.data.pagination?.currentPage || 1,
            itemsPerPage: sessionsResponse.data.pagination?.itemsPerPage || 20,
            totalPages: sessionsResponse.data.pagination?.totalPages || 1,
            total: sessionsResponse.data.pagination?.total || 0,
          };
        }

        if (statsResponse.success) {
          sessionStats.value = statsResponse.data;
        }
      } catch (error) {
        console.error('Failed to fetch sessions:', error);
        showNotification('Failed to fetch sessions', 'error');
      } finally {
        if (showLoader) loading.value = false;
      }
    };

    const exportReport = async () => {
      try {
        showNotification('Generating report...', 'success');

        await sessionApi.exportSessionReport({
          format: 'csv',
          startDate: null, // You can add date filters later
          endDate: null,
        });

        showNotification('Report exported successfully', 'success');
      } catch (error) {
        console.error('Failed to export report:', error);
        showNotification('Failed to export report', 'error');
      }
    };

    const viewSessionDetails = async session => {
      try {
        const response = await sessionApi.getSessionDetails(session.id);

        if (response.success) {
          selectedSession.value = response.data;
          showDetailsModal.value = true;
        }
      } catch (error) {
        console.error('Failed to fetch session details:', error);
        showNotification('Failed to fetch session details', 'error');
      }
    };

    const terminateSession = async session => {
      if (
        confirm(
          `Are you sure you want to terminate ${session.user.name}'s session?`
        )
      ) {
        try {
          const response = await sessionApi.terminateSession(
            session.id,
            'admin_terminated'
          );

          if (response.success) {
            showNotification('Session terminated successfully', 'success');

            // Update local state
            const index = sessions.value.findIndex(s => s.id === session.id);
            if (index !== -1) {
              sessions.value[index].status = 'expired';
            }

            if (showDetailsModal.value) {
              closeDetailsModal();
            }

            // Refresh data
            await refreshSessions(false);
          }
        } catch (error) {
          console.error('Failed to terminate session:', error);
          showNotification('Failed to terminate session', 'error');
        }
      }
    };

    const closeDetailsModal = () => {
      showDetailsModal.value = false;
      selectedSession.value = null;
    };

    // Helper methods
    const getDeviceIcon = type => {
      switch (type) {
        case 'mobile':
          return Smartphone;
        case 'tablet':
          return Tablet;
        default:
          return Monitor;
      }
    };

    const formatDate = date => {
      if (!date) return 'Unknown';
      return new Intl.DateTimeFormat('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(new Date(date));
    };

    // Pagination methods
    const previousPage = () => {
      if (pagination.value.currentPage > 1) {
        pagination.value.currentPage--;
      }
    };

    const nextPage = () => {
      if (pagination.value.currentPage < pagination.value.totalPages) {
        pagination.value.currentPage++;
      }
    };

    const goToPage = page => {
      pagination.value.currentPage = page;
    };

    // Notification helper
    const showNotification = (message, type = 'success') => {
      notification.value = {
        show: true,
        message,
        type,
      };

      // Auto hide after 5 seconds
      setTimeout(() => {
        notification.value.show = false;
      }, 5000);
    };

    // Watch for filter and pagination changes
    watch([() => pagination.value.currentPage], async () => {
      await refreshSessions();
    });

    // Debounced filter watch
    let filterTimeout;
    watch(
      [filters],
      async () => {
        clearTimeout(filterTimeout);
        filterTimeout = setTimeout(async () => {
          pagination.value.currentPage = 1;
          await refreshSessions();
        }, 500); // 500ms debounce
      },
      { deep: true }
    );

    // Lifecycle
    onMounted(async () => {
      await refreshSessions();

      // Set up auto-refresh every 30 seconds for active sessions
      refreshInterval = setInterval(() => {
        refreshSessions(false); // Refresh without showing loader
      }, 30000);
    });

    onUnmounted(() => {
      if (refreshInterval) {
        clearInterval(refreshInterval);
      }
      if (filterTimeout) {
        clearTimeout(filterTimeout);
      }
    });

    return {
      // State
      loading,
      showDetailsModal,
      selectedSession,
      sessionStats,
      sessions,
      filters,
      pagination,
      notification,

      // Computed
      visiblePages,
      formattedSessions,

      // Methods
      refreshSessions,
      exportReport,
      viewSessionDetails,
      closeDetailsModal,
      terminateSession,
      getDeviceIcon,
      formatDate,
      previousPage,
      nextPage,
      goToPage,

      // API reference for template
      sessionApi,
    };
  },
};
</script>

<style scoped>
.btn-primary {
  background-color: #2563eb;
  color: white;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s ease-in-out;
}

.btn-primary:hover {
  background-color: #1d4ed8;
}

.btn-primary:disabled {
  background-color: #9ca3af;
  cursor: not-allowed;
}

.btn-secondary {
  background-color: #f3f4f6;
  color: #374151;
  font-weight: 500;
  padding: 0.5rem 1rem;
  border-radius: 0.375rem;
  transition: background-color 0.2s ease-in-out;
}

.btn-secondary:hover {
  background-color: #e5e7eb;
}

.btn-secondary:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.form-input {
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  outline: none;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.form-input:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-select {
  display: block;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  box-shadow: 0 1px 2px 0 rgba(0, 0, 0, 0.05);
  outline: none;
  background-color: white;
  transition: border-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
}

.form-select:focus {
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.form-label {
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #374151;
  margin-bottom: 0.25rem;
}

/* Custom table styles */
.table-container {
  background: white;
  border-radius: 0.5rem;
  overflow: hidden;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.06);
}

/* Animation for notifications */
.notification-enter-active,
.notification-leave-active {
  transition: all 0.3s ease;
}

.notification-enter-from,
.notification-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* Modal backdrop animation */
.modal-backdrop-enter-active,
.modal-backdrop-leave-active {
  transition: opacity 0.3s ease;
}

.modal-backdrop-enter-from,
.modal-backdrop-leave-to {
  opacity: 0;
}

/* Modal content animation */
.modal-content-enter-active,
.modal-content-leave-active {
  transition: all 0.3s ease;
}

.modal-content-enter-from,
.modal-content-leave-to {
  opacity: 0;
  transform: scale(0.9) translateY(-20px);
}

/* Loading state styles */
.loading-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

/* Status badge styles */
.status-active {
  background-color: #d1fae5;
  color: #065f46;
}

.status-expired {
  background-color: #f3f4f6;
  color: #374151;
}

.status-suspicious {
  background-color: #fef2f2;
  color: #991b1b;
}

/* Risk level styles */
.risk-low {
  background-color: #d1fae5;
  color: #065f46;
}

.risk-medium {
  background-color: #fef3c7;
  color: #92400e;
}

.risk-high {
  background-color: #fef2f2;
  color: #991b1b;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .stats-grid {
    grid-template-columns: 1fr;
  }

  .filters-grid {
    grid-template-columns: 1fr;
  }

  .table-container {
    margin: 0 -1rem;
    border-radius: 0;
  }
}

/* Print styles */
@media print {
  .btn-primary,
  .btn-secondary {
    display: none;
  }

  .table-container {
    box-shadow: none;
    border: 1px solid #d1d5db;
  }
}
</style>
